import { useQuoteModal } from '@/hooks/useQuoteModal';

const supplyChainSteps = [
  {
    number: '01',
    icon: 'ri-building-4-line',
    title: 'Fabricación en China',
    subtitle: 'Rixin Corp',
    description:
      'El demulsificante se formula y produce en las instalaciones de Rixin Corp en China, bajo estrictos estándares de calidad internacional API y ASTM. Producto terminado, listo para usar.',
    highlight: 'Producto ya formulado',
  },
  {
    number: '02',
    icon: 'ri-shield-check-line',
    title: 'Control de Calidad',
    subtitle: 'Certificación',
    description:
      'Cada lote es sometido a pruebas de eficiencia de separación, estabilidad térmica y compatibilidad con crudos pesados. Certificación de calidad antes del embarque.',
    highlight: 'Lote certificado',
  },
  {
    number: '03',
    icon: 'ri-ship-line',
    title: 'Envío Internacional',
    subtitle: 'Logística Marítima',
    description:
      'Transporte marítimo seguro en contenedores especializados para productos químicos. Tambores de 55 galones o IBCs de 1,000 litros con sellado hermético anticorrosivo.',
    highlight: 'Transporte seguro',
  },
  {
    number: '04',
    icon: 'ri-map-pin-line',
    title: 'Llegada a Venezuela',
    subtitle: 'Aduana y Almacén',
    description:
      'Recepción en puertos venezolanos, gestión aduanal y traslado a almacenes certificados. Inventario listo para despacho inmediato a cualquier punto del país.',
    highlight: 'Puesto en Venezuela',
  },
  {
    number: '05',
    icon: 'ri-microscope-line',
    title: 'Validación Local',
    subtitle: 'Poly Alphas Chemicals C.A.',
    description:
      'Nuestro aliado técnico en Venezuela realiza pruebas físico-químicas in situ para validar la eficacia del producto según las condiciones específicas de cada campo petrolero.',
    highlight: 'Asesoría técnica',
  },
  {
    number: '06',
    icon: 'ri-oil-line',
    title: 'Entrega a Campos',
    subtitle: 'PDVSA y Operadores',
    description:
      'Entrega directa a los campos petroleros de la Faja del Orinoco: Ayacucho, Carabobo, Junín y Boyacá. Producto aprobado y listo para aplicación inmediata.',
    highlight: 'Entrega garantizada',
  },
];

export default function SupplyChainSection() {
  const { open } = useQuoteModal();
  return (
    <section id="cadena-suministro" className="bg-background-50 py-20 md:py-28 overflow-hidden">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-14 md:mb-18">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-6">
              <i className="ri-global-line text-sm w-4 h-4 flex items-center justify-center" />
              Cadena de Suministro
            </div>
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              De <span className="text-primary-500">Rixin Corp China</span>{' '}
              a sus Campos en Venezuela
            </h2>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
              Un proceso logístico consolidado que garantiza la entrega de demulsificante 
              ya formulado, certificado y validado, directamente desde el fabricante a su operación.
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Connecting line - desktop only */}
            <div className="hidden lg:block absolute top-[72px] left-[calc(16.66%+24px)] right-[calc(16.66%+24px)] h-0.5 bg-gradient-to-r from-primary-200 via-accent-200 to-secondary-200" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {supplyChainSteps.map((step, index) => (
                <div
                  key={step.number}
                  className="group relative"
                >
                  <div className="bg-background-100 rounded-lg p-6 md:p-7 border border-background-200/60 hover:border-primary-300/60 transition-all duration-300 h-full">
                    {/* Step number + icon */}
                    <div className="flex items-center gap-4 mb-5">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                        <i className={`${step.icon} text-xl`} />
                      </div>
                      <div>
                        <span className="text-primary-600 font-heading text-xs font-bold tracking-wider">
                          PASO {step.number}
                        </span>
                        <span className="block text-foreground-500 text-xs mt-0.5">
                          {step.subtitle}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">
                      {step.title}
                    </h3>
                    <p className="text-foreground-600 text-sm leading-relaxed mb-4">
                      {step.description}
                    </p>

                    {/* Highlight badge */}
                    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent-100 border border-accent-200/60 text-accent-700 text-xs font-medium">
                      <i className="ri-check-line text-xs" />
                      {step.highlight}
                    </div>
                  </div>

                  {/* Arrow connector - desktop */}
                  {index < supplyChainSteps.length - 1 && (
                    <div className="hidden lg:flex absolute -right-4 top-[72px] w-8 items-center justify-center z-10">
                      <i className="ri-arrow-right-line text-foreground-400 text-sm" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-14 md:mt-18">
            <div className="inline-flex flex-col sm:flex-row items-center gap-4">
              <button
                type="button"
                onClick={open}
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg shadow-primary-500/25"
              >
                Solicitar Cotización de Importación
                <i className="ri-arrow-right-line" />
              </button>
              <span className="text-foreground-500 text-xs">
                Producto puesto en Venezuela • Sin complicaciones logísticas
              </span>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 mt-3">
              <a href="tel:+584129080594" className="inline-flex items-center gap-1.5 text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-phone-line text-sm"></i>
                +58 412 908 0594
              </a>
              <a href="tel:+584121019460" className="inline-flex items-center gap-1.5 text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-phone-line text-sm"></i>
                +58 412 101 9460
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}